<template>
  <iColumn
    data-test="conversation-video-call"
    :width="hasCallArea ? 'fill' : 'hug'"
    :min-height="roomMinHeight"
    :theme-mode="roomThemeMode"
  >
    <iColumn
      v-if="isCallPlaceholder"
      width="fill"
      height="fill"
      align="center"
      vertical-align="middle"
      gap="small"
    >
      <iIcon name="ic:outline-videocam" size="large" />
      <iText font-size="large">
        Video Call
      </iText>
      <iText variant="subtle" font-size="small">
        Opens here when someone clicks Join Call
      </iText>
    </iColumn>
    <iText v-else-if="isOpenHere && !isLoggedIn" variant="subtle">
      Sign in to join the call.
    </iText>
    <ConversationCallRoom
      v-else-if="isOpenHere"
      :key="callTargetKey"
      :call-target="callTarget"
      :theme-mode="roomThemeMode"
      :min-height="roomMinHeight"
      @close="closeCall"
    />
  </iColumn>
</template>

<script>
import { defineAsyncComponent } from "vue";
import { DynamicElementMixin } from "%/dynamic-component-editor/mixins/dynamic-element-mixin";
import DynamicElementWithContext from "%/dynamic-component-editor/mixins/dynamic-element-with-context-mixin";
import AppearanceOptions from "%/dynamic-component-editor/bundles/appearance-options";
import BackgroundOptions from "%/dynamic-component-editor/bundles/background-options";
import LayoutWidthHeightOptions from "%/dynamic-component-editor/bundles/layout-width-height-options";
import SpacingOptions from "%/dynamic-component-editor/bundles/spacing-options";
import PositioningOptions from "%/dynamic-component-editor/bundles/positioning-options";
import MinHeight from "%/dynamic-component-editor/options/min-height";
import RoomTheme, { ROOM_THEME_MODES } from "%/dynamic-component-editor/options/room-theme";
import { useGeneralStore } from "%/stores/general-store";
import { ConversationCallElementMixin }
  from "%/dynamic-component-editor/standard-elements/conversation-call/conversation-call-element-mixin";

// The lobby needs ~530px and a joined call 480px (room-height.js); an author's
// taller Min Height wins.
const CALL_AREA_MIN_HEIGHT = 600;

// Async on purpose: the call (and livekit-client) loads only once someone joins.
const ConversationCallRoom = defineAsyncComponent(
  () => import("%/apps/video-engine/components/video-call/ConversationCallRoom.vue"),
);

export default {
  name: "ConversationVideoCall",
  components: { ConversationCallRoom },
  elementPanelSettings: {
    displayName: "Video Call",
    displayIcon: "video",
    elementType: "element",
    elementGroup: "general",
  },
  mixins: [DynamicElementMixin, DynamicElementWithContext, ConversationCallElementMixin],
  hidden: true,
  optionBundles: [
    PositioningOptions,
    new AppearanceOptions({ "default.border-radius": "large" }, { "border-radius": false }),
    new LayoutWidthHeightOptions({}, {
      "max-width": true,
      "max-height": true,
      "min-width": true,
    }),
    new SpacingOptions(),
    new BackgroundOptions({
      "default.background-color": "none",
    }, { "background-gradient": true }),
  ],
  options: [RoomTheme],
  data() {
    return {
      isRegisteredHere: false,
      registeredCallTarget: null,
    };
  },
  computed: {
    isLoggedIn() {
      return useGeneralStore().isLoggedIn;
    },
    isOpenHere() {
      return this.isRegisteredHere && Boolean(this.callTarget) && this.callStore.isOpen(this.callTarget);
    },
    hasCallArea() {
      return this.isCallPlaceholder || this.isOpenHere;
    },
    // Collapsed while closed; while open, the call area's floor or the
    // author's Min Height, whichever is taller.
    roomMinHeight() {
      if (!this.hasCallArea) {
        return undefined;
      }
      const authored = Number(this.getOptionValue(MinHeight)) || 0;

      return Math.max(CALL_AREA_MIN_HEIGHT, authored);
    },
    roomThemeMode() {
      return ROOM_THEME_MODES[this.getOptionValue(RoomTheme)] || ROOM_THEME_MODES.match;
    },
  },
  watch: {
    callTargetKey: {
      immediate: true,
      handler() {
        this.releaseRegistration();
        if (this.callTarget) {
          this.registeredCallTarget = this.callTarget;
          this.isRegisteredHere = this.callStore.registerVideoCall(this.registeredCallTarget);
        }
      },
    },
  },
  beforeUnmount() {
    this.releaseRegistration();
  },
  methods: {
    releaseRegistration() {
      if (this.isRegisteredHere && this.registeredCallTarget) {
        this.callStore.unregisterVideoCall(this.registeredCallTarget);
      }
      this.isRegisteredHere = false;
      this.registeredCallTarget = null;
    },
    closeCall() {
      if (this.callTarget) {
        this.callStore.closeCall(this.callTarget);
      }
    },
  },
};
</script>
